import {
  type FC,
  type ReactNode,
  type PropsWithChildren,
} from 'react';
import {
  Stack,
  styled,
  Typography,
} from '@mui/joy';
import {
  isEmpty
} from '@shared/lib';
import {
  type PaymentMethod
} from '../model';
import {
  PaymentMethods
} from './payment-methods.component';

const CardContent = styled(Stack)(({ theme }) => ({
  width: '100%',
  borderRadius: 12,
  marginInline: 'auto',
  height: 'fit-content',
  flexDirection: 'column',
  [theme.breakpoints.down(490)]: { borderRadius: 0 }
}));

type CardTitleBlockProps = PropsWithChildren & {
  hideTitle?: boolean;
};

const CardTitleBlock: FC<CardTitleBlockProps> = ({
  children,
  hideTitle = false
}) => (children || hideTitle) && (
  <Stack
    direction='row'
    alignItems='center'
    justifyContent='center'
    sx={{
      width: '100%',
      marginBottom: 3
    }}>
    {typeof children !== 'string' ? (
      children
    ) : (
      <Typography
        sx={({ palette }) => ({
          fontSize: 18,
          fontWeight: 400,
          lineHeight: '28px',
          fontStyle: 'normal',
          color: palette.common.white
        })}>
        {children}
      </Typography>
    )}
  </Stack>
);

const CardListsBlock = styled(Stack)(({ theme }) => ({
  rowGap: 12,
  [theme.breakpoints.down(491)]: {
    height: '100%',
    maxHeight: '100%',
  },
}));

const NoPaymentMethodsSection: FC = () => (
  <Stack
    direction='column'
    alignItems='center'
    justifyContent='center'
    sx={{
      width: '100%',
      height: '100%',
      minHeight: 392,
    }}>
    <Typography
      sx={({
        fontSize: 18,
        maxWidth: 298,
        fontWeight: 600,
        fontStyle: 'normal',
        lineHeight: 'normal',
        textAlign: 'center',
      })}>
      No payment methods are available at the moment.
    </Typography>
  </Stack>
);

export type PaymentMethodSelectorProps = Omit<CardTitleBlockProps, 'children'> & {
  title?: ReactNode;
  darkMode?: boolean;
  recently?: Maybe<PaymentMethod>;
  recommended?: Array<PaymentMethod>;
  alternative?: Array<PaymentMethod>;
  onSelect?(method: PaymentMethod): void;
};

export const PaymentMethodSelector: FC<PaymentMethodSelectorProps> = ({
  title,
  hideTitle,
  darkMode,
  recently,
  recommended,
  alternative,
  onSelect
}) => {

  const isPaymentMethodsEmpty =
    isEmpty(recently) &&
    isEmpty(recommended) &&
    isEmpty(alternative);

  return (
    <CardContent>
      <CardTitleBlock hideTitle={hideTitle}>
        {title}
      </CardTitleBlock>
      <CardListsBlock>
        {recently && (
          <PaymentMethods
            label='Recently Used'
            darkMode={darkMode}
            items={[recently]}
            onSelect={onSelect}
          />
        )}
        {(recommended && recommended.length > 0) && (
          <PaymentMethods
            label='Recommended'
            darkMode={darkMode}
            items={recommended}
            onSelect={onSelect}
          />
        )}
        {(alternative && alternative.length > 0) && (
          <PaymentMethods
            label='Alternative Payments'
            darkMode={darkMode}
            items={alternative}
            onSelect={onSelect}
          />
        )}
        {isPaymentMethodsEmpty && (
          <NoPaymentMethodsSection />
        )}
      </CardListsBlock>
    </CardContent>
  );
};